/**
 * Inventory Management — Milk Receivables API Client
 *
 * Wraps all /api/v1/milk-receivables endpoints from tms-api-inventoryManagement.
 * Requests are routed through the API gateway using the shared client.
 *
 * All responses follow the standard envelope:
 *   { success: true, data: T, meta: { requestId, timestamp } }
 *
 * The helpers here return the unwrapped `data` payload for convenience.
 */
import type { AxiosResponse } from 'axios';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type { DonorInventorySummaryResponse } from '@tms-phoenix/tms-types';
import type {
  ApiMilkReceivable,
  ApiMilkReceivableListData,
  ApiMilkReceivablesBulkResponse,
  ApiMilkProvider,
  CreateMilkReceivablePayload,
  UpdateMilkReceivablePayload,
  DiscardMilkReceivablePayload,
  ListMilkReceivablesQuery,
} from '../types/inventory-management-api.types';

// ── API client ───────────────────────────────────────────────────────────────

const API_PREFIX = API_ROUTES.INVENTORY_MANAGEMENT;
const api = getApiClient();

/** Base path for all milk-receivable routes */
const BASE = `${API_PREFIX}/milk-receivables`;

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * GET /api/v1/milk-receivables
 *
 * Returns a paginated, filtered list of milk receivables.
 * `hospital_id` is required; all other query params are optional.
 */
export async function listMilkReceivables(
  query: ListMilkReceivablesQuery,
): Promise<ApiMilkReceivableListData> {
  const params = {
    ...query,
    ...(query.search !== undefined && { search: query.search.trim().slice(0, 100) || undefined }),
  };
  const res: AxiosResponse<ApiMilkReceivableListData> =
    await api.get(BASE, { params });
  return res.data;
}

/**
 * GET /api/v1/milk-receivables/donor-inventory
 *
 * Returns a paginated, filtered list of donor milk receivables with a
 * computed_status field on each item. All bottles are returned regardless
 * of lifecycle state (never filtered by bottle status).
 */
export async function listDonorInventory(
  query: Omit<ListMilkReceivablesQuery, 'source_type'>,
): Promise<ApiMilkReceivableListData> {
  const params = {
    ...query,
    ...(query.search !== undefined && { search: query.search.trim().slice(0, 100) || undefined }),
  };
  const res: AxiosResponse<ApiMilkReceivableListData> =
    await api.get(`${BASE}/donor-inventory`, { params });
  return res.data;
}

/**
 * GET /api/v1/milk-receivables/donor-inventory/summary
 *
 * Bottle count and received volume (mL) per lifecycle status for one donor.
 * Every status is returned, with zeros where the donor has none.
 */
export async function getDonorInventorySummary(query: {
  hospital_id: string;
  provider_id: string;
}): Promise<DonorInventorySummaryResponse> {
  const res: AxiosResponse<DonorInventorySummaryResponse> =
    await api.get(`${BASE}/donor-inventory/summary`, { params: query });
  return res.data;
}

/**
 * POST /api/v1/milk-receivables
 *
 * Create a new milk receivable. Returns the created record (201).
 * Throws with code 400 if a provider exclusion covers the received_at date.
 * Throws with code 409 if receipt_number already exists for this hospital.
 *
 * The API always accepts an array of 1–50 items and always returns the bulk
 * envelope shape `{ created: ApiMilkReceivable[], failed: BulkFailure[] }`
 * (even for a single-item request) — it never returns a bare array.
 * This helper wraps the single payload in an array and unwraps the first
 * created result, surfacing the per-item failure reason if the item failed
 * server-side validation instead of throwing/being sent.
 */
export async function createMilkReceivable(
  payload: CreateMilkReceivablePayload,
): Promise<ApiMilkReceivable> {
  const res: AxiosResponse<ApiMilkReceivablesBulkResponse> =
    await api.post(BASE, [payload]);
  const body = res.data;

  // Defensive check: guard against an unexpected/malformed payload shape
  // (e.g. a proxy/gateway error page, or a future API contract change)
  // rather than assuming `.created` exists.
  if (!body || typeof body !== 'object' || !Array.isArray(body.created)) {
    console.error(
      '[milk-receivables.api] createMilkReceivable: unexpected response payload shape',
      body,
    );
    throw new Error('createMilkReceivable: API returned an empty or invalid response');
  }

  if (body.created.length === 0) {
    const failure = body.failed?.[0];
    console.error(
      '[milk-receivables.api] createMilkReceivable: item was not created',
      body.failed,
    );
    throw new Error(
      `createMilkReceivable: item was not created${failure ? ` (${failure.reason})` : ''}`,
    );
  }

  return body.created[0];
}

/**
 * POST /api/v1/milk-receivables (bulk)
 *
 * Create multiple milk receivables in a single request (1–50 items).
 * Returns created records and any per-item failures.
 */
export async function createMilkReceivablesBulk(
  payloads: CreateMilkReceivablePayload[],
): Promise<ApiMilkReceivablesBulkResponse> {
  const res: AxiosResponse<ApiMilkReceivablesBulkResponse> =
    await api.post(BASE, payloads);
  return res.data;
}

/**
 * GET /api/v1/milk-receivables/receipt/:receipt_number
 *
 * Fetch a single receivable by receipt number within a hospital.
 */
export async function getMilkReceivableByReceiptNumber(
  receiptNumber: string,
): Promise<ApiMilkReceivable> {
  const res: AxiosResponse<ApiMilkReceivable> =
    await api.get(`${BASE}/receipt/${encodeURIComponent(receiptNumber)}`);
  return res.data;
}

/**
 * GET /api/v1/milk-receivables/:receivable_id
 *
 * Fetch a single receivable by its UUID primary key.
 * @param source_type - Optional `source_type` query param: `'MOTHER'` or `'DONOR'`
 */
export async function getMilkReceivableById(
  receivableId: string,
  source_type?: 'MOTHER' | 'DONOR',
): Promise<ApiMilkReceivable> {
  const res: AxiosResponse<ApiMilkReceivable> =
    await api.get(`${BASE}/${receivableId}`, {
      params: source_type ? { source_type } : undefined,
    });
  return res.data;
}

/**
 * PATCH /api/v1/milk-receivables/:receivable_id
 *
 * Partially update a receivable. Only supplied fields are written.
 * Returns the updated record.
 * @param source_type - Optional `source_type` query param: `'MOTHER'` or `'DONOR'`
 */
export async function updateMilkReceivable(
  receivableId: string,
  payload: UpdateMilkReceivablePayload,
  source_type?: 'MOTHER' | 'DONOR',
): Promise<ApiMilkReceivable> {
  const res: AxiosResponse<ApiMilkReceivable> =
    await api.patch(`${BASE}/${receivableId}`, payload, {
      params: source_type ? { source_type } : undefined,
    });
  return res.data;
}

/**
 * DELETE /api/v1/milk-receivables/:receivable_id
 *
 * Soft-delete (discard) a receivable. Sets status = DISCARDED and
 * writes the reason to the notes field. Records are never physically removed.
 * @param source_type - Optional `source_type` query param: `'MOTHER'` or `'DONOR'`
 */
export async function discardMilkReceivable(
  receivableId: string,
  payload: DiscardMilkReceivablePayload,
  source_type?: 'MOTHER' | 'DONOR',
): Promise<ApiMilkReceivable> {
  const res: AxiosResponse<ApiMilkReceivable> =
    await api.delete(`${BASE}/${receivableId}`, {
      data: payload,
      params: source_type ? { source_type } : undefined,
    });
  return res.data;
}

/**
 * GET /api/v1/milk-receivables/convert-bottle-validation
 *
 * Validates whether a MOTHER bottle (by receipt_number) is eligible for full
 * conversion to DONOR — used by the "Multiple Mother Milk Conversion" workflow
 * to check each scanned bottle before it is listed as eligible.
 * Checks: source_type=MOTHER, not expired, status=RECEIVED, provider is an
 * APPROVED donor (person_type=BOTH), pump date on/after donor approval date,
 * and the bottle is unused. Throws (400/404) with a human-readable reason
 * when any check fails — callers should surface `err` via getApiErrorMessage.
 */
export async function validateConvertBottle(
  receiptNumber: string,
): Promise<ApiMilkReceivable> {
  const res: AxiosResponse<ApiMilkReceivable> =
    await api.get(`${BASE}/convert-bottle-validation`, {
      params: { receipt_number: receiptNumber },
    });
  return res.data;
}

/** Shape returned by PUT /milk-receivables/bulk-convert */
export interface BulkConvertMotherToDonorResult {
  /** Receivables that were successfully flipped from MOTHER to DONOR in-place */
  converted: ApiMilkReceivable[];
  /** Receivables that failed the final conversion step, with the reason */
  failed: { receivable_id: string; reason: string }[];
}

/**
 * PUT /api/v1/milk-receivables/bulk-convert
 *
 * Bulk-converts MOTHER receivables to DONOR in-place (1–25 items) as part of
 * the "Multiple Mother Milk Conversion" workflow. No new donor record is
 * created — receivable_id, volume, and provider are unchanged; only
 * source_type flips from MOTHER to DONOR. Failures are reported per-item in
 * the `failed` array without blocking the rest of the batch.
 */
export async function bulkConvertMotherToDonor(
  receivableIds: string[],
): Promise<BulkConvertMotherToDonorResult> {
  const res: AxiosResponse<BulkConvertMotherToDonorResult> =
    await api.put(
      `${BASE}/bulk-convert`,
      receivableIds.map((receivable_id) => ({ receivable_id })),
    );
  return res.data;
}

// ── Milk Providers ───────────────────────────────────────────────────────────

/**
 * GET /patient/api/v1/milkProviders/byLabel?custom_label=...
 *
 * Look up a milk provider's full profile by bottle label.
 * Resolves milk_provider_id from milk_bottle_labels, then returns the
 * same detail shape as GET /patient/api/v1/milkProviders/:person_id.
 */
export async function getMilkProviderByLabel(
  customLabel: string,
): Promise<ApiMilkProvider> {
  const res: AxiosResponse<ApiMilkProvider> =
    await api.get(`${API_ROUTES.PATIENT}/milkProviders/byLabel`, {
      params: { custom_label: customLabel },
    });
  return res.data;
}
